import { Image, StyleSheet, Text, View } from "@react-pdf/renderer"
import { IssueStatus, Report } from "graphql/types"
import React from "react"
import { formatMissingValue } from "utils/reportFormatting"
import { colors } from "../PDFExport/PDFStyles"

const styles = StyleSheet.create({
  header: {
    position: "absolute",
    top: 20,
    left: 50,
    right: 50,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
    paddingBottom: 5,
  },
  headerLeft: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  footerLogo: {
    width: 120,
    height: 25,
    objectFit: "contain",
  },
  headerCompanyName: {
    fontSize: 14,
    fontWeight: "bold",
    color: colors.primary,
    marginTop: 5,
  },
  headerCenter: {
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  headerTitle: {
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "center",
    color: colors.text,
  },
  headerSubtitle: {
    fontSize: 9,
    color: colors.darkGray,
    marginTop: 3,
  },
  headerRight: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  classificationBox: {
    borderWidth: 2,
    borderColor: "black",
    borderRadius: 3,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: "#FFF",
  },
  classificationText: {
    fontSize: 10,
    fontWeight: "bold",
    color: "black",
    textTransform: "uppercase",
  },
  issueStatus: {
    fontSize: 10,
    color: colors.darkGray,
    marginTop: 5,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    height: 50,
    borderTopWidth: 1,
    borderTopColor: colors.primary,
    paddingTop: 10,
  },
  footerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  footerLeft: {
    flexDirection: "column",
    flex: 1,
  },
  footerCenter: {
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  footerRight: {
    flexDirection: "column",
    alignItems: "flex-end",
    flex: 1,
  },
  footerText: {
    fontSize: 8,
    color: colors.darkGray,
    marginBottom: 2,
  },
  footerBold: {
    fontSize: 8,
    fontWeight: "bold",
    color: colors.text,
  },
  pageNumber: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.primary,
  },

  accreditationLogo: {
    width: 60,
    height: 30,
  },
})

interface ReportHeaderProps {
  eutName?: string
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({ eutName }) => {
  return (
    <View style={styles.header} fixed>
      <View style={styles.headerLeft}>
        <Text style={styles.headerTitle}>{formatMissingValue(eutName) || ""}</Text>
      </View>

      <View style={styles.headerRight}>
        <View style={styles.classificationBox}>
          <Text style={styles.classificationText}>NOT CLASSIFIED</Text>
        </View>
      </View>
    </View>
  )
}
interface ReportFooterProps {
  report: Report
}

export const ReportFooter: React.FC<ReportFooterProps> = ({ report }) => {
  return (
    <View style={styles.footer} fixed>
      <View style={styles.footerContent}>
        <View style={styles.footerLeft}>
          <Image src="/logo.png" style={styles.footerLogo} />
        </View>

        <View style={styles.footerCenter}>
          <Text style={styles.footerBold}>
            Document No.: {formatMissingValue(report.documentNo)}
          </Text>
          <Text style={styles.footerText}>
            Issue: {report.issueStatus === IssueStatus.DRAFT ? "DRAFT" : report.versionNumber}
          </Text>
        </View>

        <View style={styles.footerRight}>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
          <Text style={styles.footerText}>
            {new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </Text>
        </View>
      </View>
    </View>
  )
}

// Professional Cover Page Header (no classification box)
export const CoverPageHeader: React.FC<{ report: Report }> = ({ report: _report }) => {
  return (
    <View
      style={{
        ...styles.header,
        borderBottomWidth: 0,
        height: 100,
      }}
      fixed
    >
      <View style={{ alignItems: "center", width: "100%" }}>
        <Image src="/treo-logo-large.png" style={{ width: 200, height: 60 }} />
        <Text
          style={{
            fontSize: 16,
            fontWeight: "bold",
            color: "#003366",
            marginTop: 10,
          }}
        >
          Accredited Testing Laboratory
        </Text>
        <Text style={{ fontSize: 12, color: "#666", marginTop: 5 }}>
          DAkkS Registration No. D-PL-xxxxx-xx-xx
        </Text>
      </View>
    </View>
  )
}

// Simple footer for cover page
export const CoverPageFooter: React.FC = () => {
  return (
    <View
      style={{
        ...styles.footer,
        borderTopWidth: 0,
      }}
      fixed
    >
      <View style={{ alignItems: "center", width: "100%" }}>
        <Text style={{ fontSize: 9, color: "#666" }}>
          Tempowerkring 19 • D-21079 Hamburg • Tel: +49 (0)40 709 73 76 - 0
        </Text>
        <Text style={{ fontSize: 9, color: "#666" }}>info@treo-lab.de • www.treo-lab.de</Text>
      </View>
    </View>
  )
}
